import * as Tone from 'tone';

/**
 * Global 8-band EQ
 *
 * A master insert: every sound bus (chords/drone via multiNotePlayer, metronome
 * via synthPlayer) connects into getInput(), the 8 peaking filters run in series,
 * and the last band feeds Tone.Destination. Master volume stays downstream.
 *
 * Peaking filters are transparent at 0 dB, so a flat EQ leaves the sound unchanged.
 */
import { getSetting, setSetting } from '../data/userStore';

const FREQUENCIES = [60, 150, 400, 1000, 2400, 6000, 12000, 16000];
const Q = 1.1;
const STORAGE_KEY = 'globalEqGains';

class EqProcessor {
  constructor() {
    this.bands = []; // Tone.Filter peaking nodes, low -> high
    this.input = null; // first node in the chain
    this.initialized = false;
  }

  // Read the gains from the user mirror at usage time (never captured at import,
  // since the singleton is built before the mirror is hydrated).
  _loadGains() {
    const saved = getSetting(STORAGE_KEY, null);
    if (Array.isArray(saved) && saved.length === FREQUENCIES.length) {
      return saved.map(v => (typeof v === 'number' ? v : 0));
    }
    return FREQUENCIES.map(() => 0);
  }

  initialize() {
    if (this.initialized) return;

    const gainsDb = this._loadGains();
    // Peaking filters: pass only frequency/Q/gain. Leave rolloff at default so
    // the peaking response isn't distorted by cascaded biquads.
    this.bands = FREQUENCIES.map((freq, i) => new Tone.Filter({
      type: 'peaking',
      frequency: freq,
      Q,
      gain: gainsDb[i]
    }));

    for (let i = 0; i < this.bands.length - 1; i++) {
      this.bands[i].connect(this.bands[i + 1]);
    }
    this.bands[this.bands.length - 1].toDestination();
    this.input = this.bands[0];
    this.initialized = true;
  }

  /**
   * Returns the node sources should connect into. Builds the chain on first use.
   */
  getInput() {
    this.initialize();
    return this.input;
  }

  /**
   * Set the gain of a band in dB. Stored even before the chain exists so the
   * value is applied when the filters are built.
   * @param {number} index - Band index (0-7)
   * @param {number} db - Gain in decibels
   */
  setBandGain(index, db) {
    if (index < 0 || index >= FREQUENCIES.length) return;
    const gains = this._loadGains();
    gains[index] = db;
    if (this.bands[index]) {
      this.bands[index].gain.value = db;
    }
    setSetting(STORAGE_KEY, gains);
  }

  reset() {
    FREQUENCIES.forEach((_, i) => this.setBandGain(i, 0));
  }

  getFrequencies() {
    return [...FREQUENCIES];
  }

  getGains() {
    return this._loadGains();
  }
}

const eqProcessor = new EqProcessor();
export default eqProcessor;
